import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Tabs, Tab } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { queuesApi, extensionsApi, recordingsApi } from '../services/api'
import { DestinationSelector } from '../components/common'
import { useConfirm } from '../components/common/ConfirmDialog'

interface QueueMember {
  id?: string
  extension_id: string
  extension?: { extension: string; name: string }
  penalty: number
  member_type: 'static' | 'dynamic'
}

interface Queue {
  id: string
  name: string
  display_name: string
  extension: string
  description: string
  strategy: string
  timeout: number
  retry: number
  wrapuptime: number
  maxlen: number
  timeout_total: number
  announce_frequency: number
  announce_holdtime: boolean
  announce_position: boolean
  moh_sound: string
  join_announcement_id: string
  periodic_announce_id: string
  agent_announce_id: string
  weight: number
  join_empty: boolean
  leave_when_empty: boolean
  ring_in_use: boolean
  autofill: boolean
  autopause: boolean
  failover_destination_type: string
  failover_destination_id: string
  breakout_enabled: boolean
  breakout_key: string
  breakout_announcement_id: string
  breakout_destination_type: string
  breakout_destination_id: string
  breakout2_enabled: boolean
  breakout2_key: string
  breakout2_destination_type: string
  breakout2_destination_id: string
  callback_email: string
  is_active: boolean
  members: QueueMember[]
}

interface Extension {
  id: string
  extension: string
  name: string
}

const emptyForm = {
  name: '',
  display_name: '',
  extension: '',
  description: '',
  strategy: 'ringall',
  timeout: 15,
  retry: 5,
  wrapuptime: 0,
  maxlen: 0,
  timeout_total: 300,
  announce_frequency: 30,
  announce_holdtime: false,
  announce_position: true,
  moh_sound: 'default',
  join_announcement_id: '',
  periodic_announce_id: '',
  agent_announce_id: '',
  weight: 0,
  join_empty: true,
  leave_when_empty: false,
  ring_in_use: true,
  autofill: true,
  autopause: false,
  failover_destination_type: '',
  failover_destination_id: '',
  breakout_enabled: false,
  breakout_key: '1',
  breakout_announcement_id: '',
  breakout_destination_type: '',
  breakout_destination_id: '',
  breakout2_enabled: false,
  breakout2_key: '2',
  breakout2_destination_type: '',
  breakout2_destination_id: '',
  callback_email: '',
  is_active: true,
  members: [] as { extension_id: string; penalty: number; member_type: 'static' | 'dynamic' }[]
}

// Allowed destination types for queue failover/breakout
const QUEUE_ALLOWED_TYPES = [
  'extension',
  'ring_group',
  'queue',
  'ivr',
  'time_condition',
  'conference',
  'voicemail',
  'parking',
  'night_mode',
  'announcement',
  'external_dest',
  'external',
  'hangup',
  'callback',
]

const strategies = [
  { value: 'ringall', label: 'Alle gleichzeitig' },
  { value: 'leastrecent', label: 'Am längsten frei' },
  { value: 'fewestcalls', label: 'Wenigste Anrufe' },
  { value: 'random', label: 'Zufällig' },
  { value: 'rrmemory', label: 'Round Robin' },
  { value: 'rrordered', label: 'Round Robin (geordnet)' },
  { value: 'linear', label: 'Linear' },
  { value: 'wrandom', label: 'Gewichtet zufällig' }
]

const breakoutKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#']

export default function Queues() {
  const confirm = useConfirm()
  const [queues, setQueues] = useState<Queue[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingQueue, setEditingQueue] = useState<Queue | null>(null)
  const [formData, setFormData] = useState(emptyForm)

  const [extensions, setExtensions] = useState<Extension[]>([])
  const [recordings, setRecordings] = useState<{id: string, name: string}[]>([])

  useEffect(() => {
    loadQueues()
    loadRelatedData()
  }, [])

  const loadQueues = async () => {
    try {
      const response = await queuesApi.list()
      setQueues(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadRelatedData = async () => {
    try {
      const [extRes, recRes] = await Promise.all([
        extensionsApi.list(),
        recordingsApi.list()
      ])
      setExtensions(extRes.data.items || [])
      setRecordings(recRes.data.items || [])
    } catch {
      console.error('Error loading related data')
    }
  }

  const openCreateModal = () => {
    setEditingQueue(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (queue: Queue) => {
    setEditingQueue(queue)
    setFormData({
      name: queue.name || '',
      display_name: queue.display_name || '',
      extension: queue.extension || '',
      description: queue.description || '',
      strategy: queue.strategy || 'ringall',
      timeout: queue.timeout ?? 15,
      retry: queue.retry ?? 5,
      wrapuptime: queue.wrapuptime ?? 0,
      maxlen: queue.maxlen ?? 0,
      timeout_total: queue.timeout_total ?? 300,
      announce_frequency: queue.announce_frequency ?? 30,
      announce_holdtime: queue.announce_holdtime ?? false,
      announce_position: queue.announce_position ?? true,
      moh_sound: queue.moh_sound || 'default',
      join_announcement_id: queue.join_announcement_id || '',
      periodic_announce_id: queue.periodic_announce_id || '',
      agent_announce_id: queue.agent_announce_id || '',
      weight: queue.weight ?? 0,
      join_empty: queue.join_empty ?? true,
      leave_when_empty: queue.leave_when_empty ?? false,
      ring_in_use: queue.ring_in_use ?? true,
      autofill: queue.autofill ?? true,
      autopause: queue.autopause ?? false,
      failover_destination_type: queue.failover_destination_type || '',
      failover_destination_id: queue.failover_destination_id || '',
      breakout_enabled: queue.breakout_enabled ?? false,
      breakout_key: queue.breakout_key || '1',
      breakout_announcement_id: queue.breakout_announcement_id || '',
      breakout_destination_type: queue.breakout_destination_type || '',
      breakout_destination_id: queue.breakout_destination_id || '',
      breakout2_enabled: queue.breakout2_enabled ?? false,
      breakout2_key: queue.breakout2_key || '2',
      breakout2_destination_type: queue.breakout2_destination_type || '',
      breakout2_destination_id: queue.breakout2_destination_id || '',
      callback_email: queue.callback_email || '',
      is_active: queue.is_active ?? true,
      members: (queue.members || []).map(m => ({
        extension_id: m.extension_id || (m.extension as any)?.id || '',
        penalty: m.penalty ?? 0,
        member_type: (m.member_type as 'static' | 'dynamic') || 'static'
      }))
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const payload = {
        ...formData,
        // Convert empty strings to null for optional UUID fields
        join_announcement_id: formData.join_announcement_id || null,
        periodic_announce_id: formData.periodic_announce_id || null,
        agent_announce_id: formData.agent_announce_id || null,
        breakout_announcement_id: formData.breakout_announcement_id || null,
        failover_destination_type: formData.failover_destination_type || null,
        breakout_destination_type: formData.breakout_destination_type || null,
        breakout2_destination_type: formData.breakout2_destination_type || null,
      }

      if (editingQueue) {
        await queuesApi.update(editingQueue.id, payload)
        toast.success('Warteschlange aktualisiert')
      } else {
        await queuesApi.create(payload)
        toast.success('Warteschlange erstellt')
      }
      closeModal()
      loadQueues()
    } catch (err: any) {
      console.error('Save error:', err.response?.data)
      toast.error('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleDelete = async (queue: Queue) => {
    const confirmed = await confirm({
      title: 'Warteschlange löschen',
      message: `Warteschlange "${queue.display_name || queue.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await queuesApi.delete(queue.id)
      toast.success('Warteschlange gelöscht')
      loadQueues()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingQueue(null)
    setFormData(emptyForm)
  }

  const addMember = () => {
    const usedExtIds = formData.members.map(m => m.extension_id)
    const availableExt = extensions.find(e => !usedExtIds.includes(e.id))
    if (availableExt) {
      setFormData({
        ...formData,
        members: [...formData.members, { extension_id: availableExt.id, penalty: 0, member_type: 'static' }]
      })
    }
  }

  const updateMember = (index: number, field: string, value: unknown) => {
    const newMembers = [...formData.members]
    newMembers[index] = { ...newMembers[index], [field]: value }
    setFormData({ ...formData, members: newMembers })
  }

  const removeMember = (index: number) => {
    setFormData({
      ...formData,
      members: formData.members.filter((_, i) => i !== index)
    })
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Warteschlangen</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Warteschlange
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Name</th>
              <th>Strategie</th>
              <th>Mitglieder</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : queues.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Warteschlangen</td></tr>
            ) : (
              queues.map(q => (
                <tr key={q.id}>
                  <td><strong>{q.name}</strong></td>
                  <td>{q.display_name || q.name}</td>
                  <td><Badge bg="info">{strategies.find(s => s.value === q.strategy)?.label || q.strategy}</Badge></td>
                  <td><Badge bg="secondary">{(q.members || []).length}</Badge></td>
                  <td><Badge bg={q.is_active ? 'success' : 'secondary'}>{q.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(q)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(q)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingQueue ? 'Warteschlange bearbeiten' : 'Neue Warteschlange'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Tabs defaultActiveKey="general" className="mb-3">
              <Tab eventKey="general" title="Allgemein">
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nummer *</Form.Label>
                      <Form.Control
                        type="tel"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value.replace(/[^0-9]/g, '')})}
                        required
                        placeholder="z.B. 600"
                        pattern="[0-9]+"
                      />
                      <Form.Text className="text-muted">Zum Anrufen der Queue</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anzeigename</Form.Label>
                      <Form.Control
                        value={formData.display_name}
                        onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                        placeholder="z.B. Support-Team"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>BLF-Extension *</Form.Label>
                      <Form.Control
                        value={formData.extension}
                        onChange={(e) => setFormData({...formData, extension: e.target.value})}
                        placeholder="z.B. 450"
                        required
                      />
                      <Form.Text className="text-muted">Für Agent-BLF (450+Agent=45010)</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Strategie</Form.Label>
                      <Form.Select
                        value={formData.strategy}
                        onChange={(e) => setFormData({...formData, strategy: e.target.value})}
                      >
                        {strategies.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Klingeldauer (Sek.)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.timeout}
                        onChange={(e) => setFormData({...formData, timeout: parseInt(e.target.value) || 15})}
                        min={5}
                        max={300}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Retry (Sek.)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.retry}
                        onChange={(e) => setFormData({...formData, retry: parseInt(e.target.value) || 5})}
                        min={0}
                        max={60}
                      />
                      <Form.Text className="text-muted">Pause vor erneutem Versuch</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nachbearbeitungszeit</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.wrapuptime}
                        onChange={(e) => setFormData({...formData, wrapuptime: parseInt(e.target.value) || 0})}
                        min={0}
                        max={300}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max. Wartezeit (Sek.)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.timeout_total}
                        onChange={(e) => setFormData({...formData, timeout_total: parseInt(e.target.value) || 300})}
                        min={30}
                        max={3600}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="join_empty"
                        label="Beitreten wenn Queue leer"
                        checked={formData.join_empty}
                        onChange={(e) => setFormData({...formData, join_empty: e.target.checked})}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="leave_when_empty"
                        label="Anrufer entfernen wenn Queue leer wird"
                        checked={formData.leave_when_empty}
                        onChange={(e) => setFormData({...formData, leave_when_empty: e.target.checked})}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="ring_in_use"
                        label="Besetzte Agenten klingeln"
                        checked={formData.ring_in_use}
                        onChange={(e) => setFormData({...formData, ring_in_use: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="autofill"
                        label="Autofill (mehrere Anrufe gleichzeitig)"
                        checked={formData.autofill}
                        onChange={(e) => setFormData({...formData, autofill: e.target.checked})}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="autopause"
                        label="Auto-Pause bei verpasstem Anruf"
                        checked={formData.autopause}
                        onChange={(e) => setFormData({...formData, autopause: e.target.checked})}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="is_active"
                        label="Aktiv"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="members" title="Mitglieder">
                <Card className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Mitglieder (Agenten)</span>
                    <Button variant="outline-primary" size="sm" onClick={addMember} disabled={formData.members.length >= extensions.length}>
                      <i className="bi bi-plus-lg me-1"></i> Mitglied hinzufügen
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div className="alert alert-info mb-3">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Statisch:</strong> Immer angemeldet. <strong>Dynamisch:</strong> Kann sich mit Feature-Code *45{'{'}Queue{'}'} an-/abmelden.
                    </div>
                    {formData.members.length === 0 ? (
                      <p className="text-muted mb-0">Keine Mitglieder. Klicken Sie auf "Mitglied hinzufügen".</p>
                    ) : (
                      formData.members.map((member, index) => (
                        <Row key={index} className="mb-2 align-items-end">
                          <Col md={5}>
                            <Form.Group>
                              <Form.Label className="small">Nebenstelle</Form.Label>
                              <Form.Select
                                value={member.extension_id}
                                onChange={(e) => updateMember(index, 'extension_id', e.target.value)}
                              >
                                <option value="">-- Auswählen --</option>
                                {extensions.map(ext => (
                                  <option key={ext.id} value={ext.id}>{ext.extension} - {ext.name}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="small">Typ</Form.Label>
                              <Form.Select
                                value={member.member_type}
                                onChange={(e) => updateMember(index, 'member_type', e.target.value)}
                              >
                                <option value="static">Statisch</option>
                                <option value="dynamic">Dynamisch</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small">Priorität</Form.Label>
                              <Form.Control
                                type="number"
                                value={member.penalty}
                                onChange={(e) => updateMember(index, 'penalty', parseInt(e.target.value) || 0)}
                                min={0}
                                max={99}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Button variant="outline-danger" size="sm" onClick={() => removeMember(index)}>
                              <i className="bi bi-trash"></i>
                            </Button>
                          </Col>
                        </Row>
                      ))
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              <Tab eventKey="announcements" title="Ansagen">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Begrüssungsansage</Form.Label>
                      <Form.Select
                        value={formData.join_announcement_id}
                        onChange={(e) => setFormData({...formData, join_announcement_id: e.target.value})}
                      >
                        <option value="">-- Keine --</option>
                        {recordings.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Wird beim Eintritt in die Queue abgespielt</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Periodische Ansage</Form.Label>
                      <Form.Select
                        value={formData.periodic_announce_id}
                        onChange={(e) => setFormData({...formData, periodic_announce_id: e.target.value})}
                      >
                        <option value="">-- Keine --</option>
                        {recordings.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Wird periodisch während des Wartens abgespielt</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ansage-Intervall (Sek.)</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.announce_frequency}
                        onChange={(e) => setFormData({...formData, announce_frequency: parseInt(e.target.value) || 0})}
                        min={0}
                        max={600}
                      />
                      <Form.Text className="text-muted">0 = deaktiviert</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ansage an Agent</Form.Label>
                      <Form.Select
                        value={formData.agent_announce_id}
                        onChange={(e) => setFormData({...formData, agent_announce_id: e.target.value})}
                      >
                        <option value="">-- Keine --</option>
                        {recordings.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Wird dem Agent vor dem Verbinden abgespielt</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Card className="mb-3">
                  <Card.Header>Positions- und Wartezeit-Ansagen</Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            id="announce_position"
                            label="Position in Queue ansagen"
                            checked={formData.announce_position}
                            onChange={(e) => setFormData({...formData, announce_position: e.target.checked})}
                          />
                          <Form.Text className="text-muted">
                            "Sie sind Anrufer Nummer X in der Warteschlange"
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            id="announce_holdtime"
                            label="Geschätzte Wartezeit ansagen"
                            checked={formData.announce_holdtime}
                            onChange={(e) => setFormData({...formData, announce_holdtime: e.target.checked})}
                          />
                          <Form.Text className="text-muted">
                            "Ihre geschätzte Wartezeit beträgt X Minuten"
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Tab>

              <Tab eventKey="breakout" title="Breakout">
                <Card className="mb-3">
                  <Card.Header>
                    <Form.Check
                      type="switch"
                      id="breakout_enabled"
                      label="Breakout aktivieren"
                      checked={formData.breakout_enabled}
                      onChange={(e) => setFormData({...formData, breakout_enabled: e.target.checked})}
                    />
                  </Card.Header>
                  <Card.Body>
                    <p className="text-muted mb-3">
                      Ermöglicht Anrufern, durch Drücken einer Taste die Warteschlange zu verlassen und zu einem anderen Ziel weitergeleitet zu werden.
                    </p>

                    {formData.breakout_enabled && (
                      <>
                        <Row>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Taste zum Verlassen</Form.Label>
                              <Form.Select
                                value={formData.breakout_key}
                                onChange={(e) => setFormData({...formData, breakout_key: e.target.value})}
                              >
                                {breakoutKeys.map(k => (
                                  <option key={k} value={k}>{k}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={8}>
                            <Form.Group className="mb-3">
                              <Form.Label>Breakout-Ansage</Form.Label>
                              <Form.Select
                                value={formData.breakout_announcement_id}
                                onChange={(e) => setFormData({...formData, breakout_announcement_id: e.target.value})}
                              >
                                <option value="">-- Standard-Ansage --</option>
                                {recordings.map(r => (
                                  <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                z.B. "Drücken Sie 1 um eine Nachricht zu hinterlassen"
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>Breakout-Ziel</Form.Label>
                          <DestinationSelector
                            destinationType={formData.breakout_destination_type}
                            destinationValue={formData.breakout_destination_id}
                            onChange={(type, value) => setFormData({
                              ...formData,
                              breakout_destination_type: type,
                              breakout_destination_id: value
                            })}
                            allowedTypes={QUEUE_ALLOWED_TYPES}
                            excludeType="queue"
                            excludeId={editingQueue?.id}
                            allowExternal={true}
                            placeholder="Breakout-Ziel auswählen..."
                          />
                        </Form.Group>
                      </>
                    )}
                  </Card.Body>
                </Card>

                {/* Zweite Breakout-Option */}
                <Card className="mb-3">
                  <Card.Header>
                    <Form.Check
                      type="switch"
                      id="breakout2_enabled"
                      label="Zweite Breakout-Taste aktivieren"
                      checked={formData.breakout2_enabled}
                      onChange={(e) => setFormData({...formData, breakout2_enabled: e.target.checked})}
                    />
                  </Card.Header>
                  <Card.Body>
                    <p className="text-muted mb-3">
                      Zusätzliche Option für Anrufer, z.B. "Drücken Sie 2 um einen Rückruf anzufordern".
                    </p>

                    {formData.breakout2_enabled && (
                      <>
                        <Row>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Taste für Option 2</Form.Label>
                              <Form.Select
                                value={formData.breakout2_key}
                                onChange={(e) => setFormData({...formData, breakout2_key: e.target.value})}
                              >
                                {breakoutKeys.filter(k => k !== formData.breakout_key).map(k => (
                                  <option key={k} value={k}>{k}</option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Taste {formData.breakout_key} ist bereits für Option 1 belegt
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>Ziel für Taste {formData.breakout2_key}</Form.Label>
                          <DestinationSelector
                            destinationType={formData.breakout2_destination_type}
                            destinationValue={formData.breakout2_destination_id}
                            onChange={(type, value) => setFormData({
                              ...formData,
                              breakout2_destination_type: type,
                              breakout2_destination_id: value
                            })}
                            allowedTypes={QUEUE_ALLOWED_TYPES}
                            excludeType="queue"
                            excludeId={editingQueue?.id}
                            allowExternal={true}
                            placeholder="Breakout-Ziel 2 auswählen..."
                          />
                        </Form.Group>
                      </>
                    )}
                  </Card.Body>
                </Card>

                {/* Callback Info - erscheint wenn CALLBACK als Ziel gewählt wurde */}
                {(formData.breakout_destination_type === 'CALLBACK' || formData.breakout2_destination_type === 'CALLBACK') && (
                  <Card className="mb-3 border-success">
                    <Card.Header className="bg-success text-white">
                      <i className="bi bi-envelope me-2"></i>Rückruf-Benachrichtigung
                    </Card.Header>
                    <Card.Body>
                      <div className="alert alert-info mb-3">
                        <i className="bi bi-info-circle me-2"></i>
                        Die Rückruf-Benachrichtigung wird automatisch an alle Warteschlangen-Mitglieder gesendet,
                        bei denen eine E-Mail-Adresse in der Nebenstelle hinterlegt ist.
                      </div>
                      <Form.Group className="mb-0">
                        <Form.Label>Zusätzliche E-Mail-Adresse (optional)</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.callback_email}
                          onChange={(e) => setFormData({...formData, callback_email: e.target.value})}
                          placeholder="team@firma.de"
                        />
                        <Form.Text className="text-muted">
                          Falls keine Mitglieder-E-Mails vorhanden sind oder als zusätzlicher Empfänger.
                        </Form.Text>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                )}
              </Tab>

              <Tab eventKey="failover" title="Failover">
                <Card className="mb-3">
                  <Card.Header>
                    <i className="bi bi-arrow-return-right me-2"></i>Bei Timeout / keine Antwort
                  </Card.Header>
                  <Card.Body>
                    <p className="text-muted mb-3">
                      Ziel nach Ablauf der maximalen Wartezeit ({formData.timeout_total} Sekunden) oder wenn alle Agenten nicht erreichbar sind.
                    </p>
                    <Form.Group>
                      <DestinationSelector
                        destinationType={formData.failover_destination_type}
                        destinationValue={formData.failover_destination_id}
                        onChange={(type, value) => setFormData({
                          ...formData,
                          failover_destination_type: type,
                          failover_destination_id: value
                        })}
                        allowedTypes={QUEUE_ALLOWED_TYPES}
                        excludeType="queue"
                        excludeId={editingQueue?.id}
                        allowExternal={true}
                        placeholder="Failover-Ziel auswählen..."
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingQueue ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
